import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppleAuth } from '@/common/hooks/apple-auth';
import { isAppleSignInAvailable } from '@/common/lib/apple-sign-in';
import { AppleLogo } from '@/components/Auth/Buttons/AppleLoginButton';
import { Loader } from '@/components/ui/Loader/Loader';
import { useAppSelector } from '@/redux/store';

/**
 * Profile card of the iOS app: a guest's game lives only on this device until
 * it is linked to an Apple ID, so say that quietly and offer the link; once
 * linked, just show that it is.
 */
const AppleAccount: FC = () => {
  const { t } = useTranslation();
  const { isLoading, link } = useAppleAuth();
  const isLinked = useAppSelector((state) => !!state.user.user?.appleLinked);

  if (!isAppleSignInAvailable()) return null;

  if (isLinked) {
    return (
      <div className="mx-2 mb-4 flex items-center justify-center gap-2 text-sm text-white/55">
        <AppleLogo className="w-3.5 h-3.5 -mt-0.5" />
        {t('Your progress is saved to your Apple ID')}
      </div>
    );
  }

  return (
    <div className="mx-2 mb-4 rounded-2xl bg-[#2C2D33] p-4">
      <p className="text-sm leading-snug text-white/70 mb-3">
        {t(
          'You are playing as a guest: your progress is kept only on this device. Link an Apple ID to restore it after reinstalling or on a new iPhone.',
        )}
      </p>
      <button
        type="button"
        onClick={() => void link()}
        disabled={isLoading}
        className="w-full h-11 flex items-center justify-center gap-2 rounded-xl bg-white text-black font-semibold active:scale-[0.98] transition-transform duration-150 ease-in-out disabled:opacity-70"
      >
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <AppleLogo className="w-4 h-4 -mt-0.5" />
            {t('Link Apple ID')}
          </>
        )}
      </button>
    </div>
  );
};

export { AppleAccount };
